import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { getUserData, getUserRole } from '../../utils/getUserInfo'
import { isAdminGroup } from '../../utils/adminGroup'
import { prettifyGroup } from '../../utils/groupDisplay'
import { getUserRolePermissions } from '../../services/userServicer'
import './Sidebar.css'
import { IoHomeOutline } from 'react-icons/io5'
import {
  LuComponent, LuLayers, LuTicket, LuUserCog, LuDatabase, LuSettings, LuShield, LuLayoutDashboard, LuActivity,
  LuBuilding2, LuGrid, LuNetwork, LuShieldCheck, LuFileBarChart, LuListChecks, LuBookOpen } from 'react-icons/lu'
import { PiCertificate } from 'react-icons/pi'
import { CiViewTable } from 'react-icons/ci'
import React from 'react'

import { FaBullseye, FaLink, FaSitemap, FaExternalLinkAlt, FaFileAlt, FaChevronDown, FaChevronRight, FaTasks, FaBuilding, FaServer, FaBriefcase, FaCalendarAlt, FaCompass, FaDollarSign, FaExclamationTriangle, FaCodeBranch, FaShieldAlt, FaLayerGroup, FaProjectDiagram, FaList, FaRobot, FaFolder, FaCheckSquare, FaClipboardCheck, FaHistory, FaBook, FaChartBar, FaBullhorn, FaUsers, FaCheck } from 'react-icons/fa'

import { getV2Token } from '../../utils/v2AuthUtils'
import { DCC_ORM_URL } from '../../utils/dccApp'
import { AiTwotoneSecurityScan } from 'react-icons/ai'
import { CONFIGURATION_SECTION_NAV } from '../../navigation/configurationSectionNav'
import { useTenant } from '../../context/TenantContext'
import { capabilitiesFor } from '../../types/rootEntity'
// Partner Portal sidebar (partnerLinks array + PartnerSidebarBadge) removed —
// the Partner Portal now lives in the standalone partner_portal_frontend app.

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  language: 'en' | 'ar'
  /** Mobile drawer open state (controlled by the layout / header hamburger). */
  mobileOpen?: boolean
  onMobileClose?: () => void
}

interface SidebarLink {
  page: string
  labelKey: string
  /** Optional muted one-line hint under the label (expanded sidebar / mobile). */
  descriptionKey?: string
  href: string
  icon: (props: any) => React.ReactElement
  external?: boolean
  externalUrl?: string | null
  /** External links open in a new tab by default. Set this for a sibling app that shares
   *  our session (the DCC), where a new tab per jump just piles up duplicates. */
  openInSameTab?: boolean
  children?: SidebarLink[]
  /** When set, only show this link if the caller's archetype has the named
   *  capability per src/types/rootEntity.ts. */
  archetypeGate?: 'hasPractitioners' | 'hasPlaybookIp' | 'ownsEvidenceVault' | 'hasAuditWorkspace' | 'canFederateOutbound' | 'canFederateInbound' | 'canManagePartners' | 'canManageMemberships' | 'canManageAllTenants'
  /** Phase 4: when true, only show this link if the caller's tenant has
   *  subtree scope (non-leaf tenant) — leaf branches have nothing to administer. */
  requiresSubtree?: boolean
  /** Phase 5: when true, only show this link if the caller's tenant HAS a
   *  parent (tenantDepth > 0) — root tenants aren't branches. */
  requiresSubTenantDepth?: boolean
  /** When true, only an admin group sees this link (same isFullAccess test used below).
   *  For screens whose endpoints refuse non-admins no matter what page permission a group
   *  has been granted — Integration Settings is the first of them. */
  adminOnly?: boolean
  /** Names the per-page permission (matched via getUserRolePermissions(), same
   *  lookup usePagePermission uses) that this link fronts. The sidebar HIDES a
   *  link whose permission the caller lacks `show` on — see the permission gate
   *  in the component below. A link with no `permissionPage` is never hidden,
   *  so anything gated must name its page here or it stays visible to everyone.
   *  An array means the link fronts several screens, such as the ORM app; any
   *  one of them granting `show` earns the link. */
  permissionPage?: string | string[]
  /** Shown to everyone but greyed out and not clickable, with a "you don't have
   *  access" tooltip — for a module this deployment does not include, so users see it
   *  exists rather than wondering where it went. Skips the permission gate. */
  unavailable?: boolean
}

/**
 * The DCC pages the ORM app is made of, documented here as the permissionPage for the
 * ORM sidebar entry (informational only — see SidebarLink.permissionPage). Names match
 * the backend catalog (FRONTEND_PAGES in new_adib_backend/src/group/page-catalog.ts).
 */
const ORM_PERMISSION_PAGES = [
  'RCM',
  'Risk Catalog',
  'Control Catalog',
  'KRI Catalog',
  'KRI Heatmap',
  'Incidents Catalog',
  'ActionPlan',
]

// Partner Portal sidebar (partnerLinks) removed — moved to the standalone
// partner_portal_frontend app.

/** Icons for Configuration submenu (must match CONFIGURATION_SECTION_NAV pages) */
const configurationSectionIconByPage: Record<string, (props: any) => React.ReactElement> = {
  'configuration-dashboard': (props: any) => <LuLayoutDashboard {...props} />,
  'tenable-integration': (props: any) => <LuShieldCheck {...props} />,
  'document-categories': (props: any) => <FaFileAlt {...props} />,
  'document-workflows': (props: any) => <FaFileAlt {...props} />,
  'document-approval': (props: any) => <FaFileAlt {...props} />,
  'services-status': (props: any) => <LuSettings {...props} />,
  Activities: (props: any) => <FaTasks {...props} />,
  ControlReferencePage: (props: any) => <FaLink {...props} />,
  'FinancialImpactsPage': (props: any) => <FaDollarSign {...props} />,
  EventTypePage: (props: any) => <FaCalendarAlt {...props} />,
  AccountTypePage: (props: any) => <FaBuilding {...props} />,
  process: (props: any) => <FaTasks {...props} />,
  CreateSubBusinessUnitPage: (props: any) => <FaBuilding {...props} />,
  CreateSystem: (props: any) => <FaServer {...props} />,
  CreateJobTitle: (props: any) => <FaBriefcase {...props} />,
  CreateDiscoveredType: (props: any) => <FaCompass {...props} />,
  CreateCurrencyPage: (props: any) => <FaDollarSign {...props} />,
  CreateRootCause: (props: any) => <FaExclamationTriangle {...props} />,
  CreateSubFunction: (props: any) => <FaCodeBranch {...props} />,
  CreateRiskCategory: (props: any) => <FaShieldAlt {...props} />,
  'lifecycle-stages': (props: any) => <LuSettings {...props} />,
  criticalities: (props: any) => <LuSettings {...props} />,
  'asset-types': (props: any) => <LuSettings {...props} />,
  'asset-classifications': (props: any) => <LuSettings {...props} />,
  'data-handled': (props: any) => <LuSettings {...props} />,
  function: (props: any) => <PiCertificate {...props} />,
  'impact-area': (props: any) => <FaLayerGroup {...props} />,
  'policy-categories': (props: any) => <FaFolder {...props} />,
  'policy-settings': (props: any) => <LuSettings {...props} />,
  'llm-routing': (props: any) => <FaRobot {...props} />,
}

const configurationSidebarChildren: SidebarLink[] = CONFIGURATION_SECTION_NAV.map((item) => ({
  page: item.page,
  labelKey: item.labelKey,
  href: item.href,
  adminOnly: item.adminOnly,
  permissionPage: item.permissionPage,
  icon: configurationSectionIconByPage[item.page] ?? ((props: any) => <LuSettings {...props} />),
}))

// Update links array to include icon components as functions
// Order: Home → Compliance → Enterprise → Catalogs → Data (Document) → Reporting → User Settings → Configuration
const links: SidebarLink[] = [
  {
    // Never gated by permission: Home is the one guaranteed escape route
    // (see ModuleGate.tsx's ALWAYS_ALLOW — same principle applies here).
    page: 'dashboard-home',
    labelKey: 'Sidebar.Home',
    href: '/',
    icon: (props: any) => <IoHomeOutline {...props} />,
  },
  {
    page: 'trustlens',
    labelKey: 'Sidebar.TrustLens',
    href: '#',
    icon: (props: any) => <LuShieldCheck {...props} />,
    children: [
      { page: 'trustlens-dashboard', labelKey: 'Sidebar.TrustLensDashboard', href: '/trustlens/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'trustlens-profile', labelKey: 'Sidebar.TrustLensProfile', href: '/trustlens/profile', icon: (props: any) => <FaBuilding {...props} /> },
      { page: 'trustlens-assessment', labelKey: 'Sidebar.TrustLensAssessment', href: '/trustlens/assessment', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'trustlens-l1', labelKey: 'Sidebar.TrustLensL1', href: '/trustlens/posture/l1', icon: (props: any) => <FaBullseye {...props} /> },
      { page: 'trustlens-l2', labelKey: 'Sidebar.TrustLensL2', href: '/trustlens/posture/l2', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'trustlens-l3', labelKey: 'Sidebar.TrustLensL3', href: '/trustlens/posture/l3', icon: (props: any) => <LuBookOpen {...props} /> },
      { page: 'trustlens-l4', labelKey: 'Sidebar.TrustLensL4', href: '/trustlens/posture/l4', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'trustlens-l5', labelKey: 'Sidebar.TrustLensL5', href: '/trustlens/posture/l5', icon: (props: any) => <LuShieldCheck {...props} /> },
      { page: 'trustlens-report', labelKey: 'Sidebar.TrustLensReport', href: '/trustlens/report', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'trustlens-invitations', labelKey: 'Sidebar.TrustLensInvitations', href: '/trustlens/invitations', icon: (props: any) => <FaUsers {...props} /> },
    ],
  },
  {
    // Financial Crime: sanctions and third-party screening (src/pages/FinancialCrime)
    page: 'financial-crime',
    labelKey: 'Sidebar.FinancialCrime',
    href: '#',
    icon: (props: any) => <FaShieldAlt {...props} />,
    children: [
      { page: 'financial-crime-overview', labelKey: 'Sidebar.FinancialCrimeOverview', href: '/financial-crime', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'financial-crime-search', labelKey: 'Sidebar.FinancialCrimeSearch', href: '/financial-crime/search', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'financial-crime-counterparties', labelKey: 'Sidebar.FinancialCrimeCounterparties', href: '/financial-crime/counterparties', icon: (props: any) => <FaBuilding {...props} /> },
      { page: 'financial-crime-alerts', labelKey: 'Sidebar.FinancialCrimeAlerts', href: '/financial-crime/alerts', icon: (props: any) => <FaExclamationTriangle {...props} /> },
      { page: 'financial-crime-tasks', labelKey: 'Sidebar.FinancialCrimeTasks', href: '/financial-crime/tasks', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'financial-crime-approvals', labelKey: 'Sidebar.FinancialCrimeApprovals', href: '/financial-crime/approvals', icon: (props: any) => <FaCheckSquare {...props} /> },
      { page: 'financial-crime-settings', labelKey: 'Sidebar.FinancialCrimeSettings', href: '/financial-crime/settings', icon: (props: any) => <LuSettings {...props} /> },
    ],
  },
  {
    page: 'compliance',
    labelKey: 'Sidebar.ComplianceMenu',
    href: '#',
    icon: (props: any) => <LuComponent {...props} />,
    children: [
      { page: 'standards', labelKey: 'Sidebar.Reference', href: '/standards', icon: (props: any) => <CiViewTable {...props} />, permissionPage: 'standards' },
      // No "Create …" entries here: creating is reached from the list page's
      // own Create button (Compliance.tsx, Surveys.tsx), so a second route into
      // the same form only lengthens the menu. The routes themselves stay.
      { page: 'compliances', labelKey: 'Sidebar.Compliance', href: '/compliances', icon: (props: any) => <LuComponent {...props} />, permissionPage: 'compliances' },
      /* 
      e menu — hidden from sidebar; re-enable when needed.
         The pages and their routes (/policy, /policy/obligations, …) all stay
         live in main.tsx, so the screens remain reachable by direct URL.
         Policy Categories is hidden too — commented out in CONFIGURATION_SECTION_NAV.
      {
        page: 'policy-module',
        labelKey: 'Sidebar.PolicyModule',
        href: '#',
        icon: (props: any) => <FaFileAlt {...props} />,
        children: [
          { page: 'policy-library', labelKey: 'Sidebar.PolicyLibrary', href: '/policy', icon: (props: any) => <FaFileAlt {...props} /> },
          { page: 'policy-obligations', labelKey: 'Sidebar.RegulatoryObligations', href: '/policy/obligations', icon: (props: any) => <PiCertificate {...props} /> },
          { page: 'policy-approvals', labelKey: 'Sidebar.ApprovalWorkflow', href: '/policy/approvals', icon: (props: any) => <FaCheckSquare {...props} /> },
          { page: 'policy-attestation', labelKey: 'Sidebar.Attestation', href: '/policy/attestation', icon: (props: any) => <FaClipboardCheck {...props} /> },
          { page: 'policy-versions', labelKey: 'Sidebar.PolicyVersionControl', href: '/policy/versions', icon: (props: any) => <FaHistory {...props} /> },
        ],
      },
      */
      {
        page: 'survey-module',
        labelKey: 'Sidebar.Survey',
        href: '#',
        icon: (props: any) => <PiCertificate {...props} />,
        children: [
          { page: 'surveys-list', labelKey: 'Sidebar.Surveys', href: '/surveys', icon: (props: any) => <FaList {...props} />, permissionPage: 'survey' },
          { page: 'bank-questions', labelKey: 'Sidebar.BankQuestions', href: '/bankQuestions', icon: (props: any) => <FaClipboardCheck {...props} />, permissionPage: 'survey' },
        ],
      },
      { page: 'rcsa', labelKey: 'Sidebar.RCSA', href: '/rcsa', icon: (props: any) => <FaChartBar {...props} />, permissionPage: 'RCSA' },
      { page: 'remediation', labelKey: 'Sidebar.Remediation', href: '/remediation', icon: (props: any) => <FaTasks {...props} />, permissionPage: 'Remediation' },
      // Impact areas moved to the Configuration submenu (CONFIGURATION_SECTION_NAV).
    ],
  },
  // SLA moved down — it now sits between Workflow and Configuration.
  /** Supplemental compliance hub (EG CERT–style shortcuts: dashboard, reports, standards, tickets, etc.) */
 
  // {
  //   page: 'enterprise-risk-management',
  //   labelKey: 'Sidebar.EnterpriseRiskManagement',
  //   href: '#',
  //   icon: (props: any) => <FaExternalLinkAlt {...props} />,
  //   external: true,
  //   externalUrl: null
  // },
  {
    // Jump to the DCC (adib-frontend) ORM app. Its home dashboard IS the ORM surface —
    // RCM, Risks/Controls/KRIs catalogs, Incidents, Action Plans — so there is no deeper
    // path to point at. Same tab, not a new one: the two apps share one session cookie and
    // the user moves back and forth, so new tabs would only accumulate.
    //
    // Gated on the ORM screens themselves rather than a page named "ORM": permission rows
    // come from the shared backend catalog (FRONTEND_PAGES in new_adib_backend's
    // group/page-catalog.ts), and a page absent from a group's rows cannot even be granted
    // from the permissions matrix — so inventing a row here would ship a link nobody can
    // turn on. ANY of these granting `show` reveals the link, matching how the DCC's own
    // home decides whether to show its Reporting card.
    page: 'orm-dcc',
    labelKey: 'Sidebar.ORM',
    href: DCC_ORM_URL,
    icon: (props: any) => <FaExternalLinkAlt {...props} />,
    external: true,
    externalUrl: DCC_ORM_URL,
    openInSameTab: true,
    permissionPage: ORM_PERMISSION_PAGES,
    // Not part of this deployment: listed, but disabled with an explanation.
    unavailable: true,
  },
  {
    page: 'catalogs',
    labelKey: 'Sidebar.Catalogs',
    href: '#',
    icon: (props: any) => <LuGrid {...props} />,
    children: [
      { page: 'services-catalog', labelKey: 'Sidebar.ServicesCatalog', href: '/catalogs/services', icon: (props: any) => <LuLayers {...props} />, permissionPage: 'Service Catalog' },
      { page: 'resources', labelKey: 'Sidebar.ResourcesManagement', href: '/catalogs/resources', icon: (props: any) => <LuLayers {...props} />, permissionPage: 'Resources' },
      { page: 'controls', labelKey: 'Sidebar.ControlCatalog', href: '/controls', icon: (props: any) => <FaShieldAlt {...props} />, permissionPage: 'Control Catalog' },
    ],
  },
  {
    page: 'documents',
    labelKey: 'Sidebar.DocumentManagement',
    href: '/documents',
    icon: (props: any) => <FaFileAlt {...props} />,
    permissionPage: 'Document Management',
  },
  // Reporting System lives in the ORM app (adib-frontend2), not here.
  // {
  //   page: 'reporting',
  //   labelKey: 'Sidebar.ReportingSystem',
  //   href: '/reporting',
  //   icon: (props: any) => <FaChartBar {...props} />
  // },
  // Partner Portal sidebar section removed — moved to the standalone partner_portal_frontend app.
  /* Internal Audit menu — hidden from sidebar; re-enable when needed
  {
    page: 'audit',
    labelKey: 'Sidebar.Audit',
    href: '#',
    icon: (props: any) => <LuActivity {...props} />,
    children: [
      { page: 'audit-activity-log', labelKey: 'Sidebar.ActivityLog', href: '/audit/activity-log', icon: (props: any) => <FaHistory {...props} /> },
      { page: 'iams-dashboard', labelKey: 'Sidebar.IamsDashboard', href: '/audit/iams', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'iams-canvas', labelKey: 'Sidebar.IamsCanvas', href: '/audit/iams/canvas', icon: (props: any) => <FaProjectDiagram {...props} /> },
      { page: 'iams-universe', labelKey: 'Sidebar.IamsUniverse', href: '/audit/iams/universe', icon: (props: any) => <FaCompass {...props} /> },
      { page: 'iams-plan-composer', labelKey: 'Sidebar.IamsPlanComposer', href: '/audit/iams/plan-composer', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'iams-scenario', labelKey: 'Sidebar.IamsScenario', href: '/audit/iams/scenario-planner', icon: (props: any) => <FaCodeBranch {...props} /> },
      { page: 'iams-capacity', labelKey: 'Sidebar.IamsCapacity', href: '/audit/iams/capacity', icon: (props: any) => <FaUsers {...props} /> },
      { page: 'iams-engagements', labelKey: 'Sidebar.IamsEngagements', href: '/audit/iams/engagements', icon: (props: any) => <FaClipboardCheck {...props} /> },
      { page: 'iams-fieldwork', labelKey: 'Sidebar.IamsFieldwork', href: '/audit/iams/fieldwork', icon: (props: any) => <LuActivity {...props} /> },
      { page: 'iams-checklists', labelKey: 'Sidebar.IamsChecklists', href: '/audit/iams/checklists', icon: (props: any) => <FaCheckSquare {...props} /> },
      { page: 'iams-rc-library', labelKey: 'Sidebar.IamsRcLibrary', href: '/audit/iams/rc-library', icon: (props: any) => <FaBook {...props} /> },
      { page: 'iams-test-plan', labelKey: 'Sidebar.IamsTestPlan', href: '/audit/iams/test-plan', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'iams-working-paper', labelKey: 'Sidebar.IamsWorkingPaper', href: '/audit/iams/working-paper', icon: (props: any) => <FaFileAlt {...props} /> },
      { page: 'iams-evidence', labelKey: 'Sidebar.IamsEvidence', href: '/audit/iams/evidence', icon: (props: any) => <FaFolder {...props} /> },
      { page: 'iams-anomaly', labelKey: 'Sidebar.IamsAnomaly', href: '/audit/iams/anomaly-triage', icon: (props: any) => <FaExclamationTriangle {...props} /> },
      { page: 'iams-review-queue', labelKey: 'Sidebar.IamsReviewQueue', href: '/audit/iams/review-queue', icon: (props: any) => <LuListChecks {...props} /> },
      { page: 'iams-findings', labelKey: 'Sidebar.IamsFindings', href: '/audit/iams/findings', icon: (props: any) => <FaExclamationTriangle {...props} /> },
      { page: 'iams-action-plans', labelKey: 'Sidebar.IamsActionPlans', href: '/audit/iams/action-plans', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'iams-risk-updates', labelKey: 'Sidebar.IamsRiskUpdates', href: '/audit/iams/risk-updates', icon: (props: any) => <FaShieldAlt {...props} /> },
      { page: 'iams-monitor', labelKey: 'Sidebar.IamsMonitor', href: '/audit/iams/monitor', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-analytics', labelKey: 'Sidebar.IamsAnalytics', href: '/audit/iams/analytics', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-eng-report', labelKey: 'Sidebar.IamsEngReport', href: '/audit/iams/engagement-report', icon: (props: any) => <FaFileAlt {...props} /> },
      { page: 'iams-ac-pack', labelKey: 'Sidebar.IamsAcPack', href: '/audit/iams/ac-pack', icon: (props: any) => <FaBriefcase {...props} /> },
      { page: 'iams-board-pack', labelKey: 'Sidebar.IamsBoardPack', href: '/audit/iams/board-pack', icon: (props: any) => <FaBuilding {...props} /> },
      { page: 'iams-ac-dashboard', labelKey: 'Sidebar.IamsAcDashboard', href: '/audit/iams/ac-dashboard', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'iams-ac-plan-review', labelKey: 'Sidebar.IamsAcPlanReview', href: '/audit/iams/ac-plan-review', icon: (props: any) => <FaCalendarAlt {...props} /> },
      { page: 'iams-auditee-portal', labelKey: 'Sidebar.IamsAuditeePortal', href: '/audit/iams/auditee-portal', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'iams-auditee-requests', labelKey: 'Sidebar.IamsAuditeeRequests', href: '/audit/iams/auditee-requests', icon: (props: any) => <FaTasks {...props} /> },
      { page: 'iams-auditee-notify', labelKey: 'Sidebar.IamsAuditeeNotify', href: '/audit/iams/auditee-notify', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'iams-agent-runs', labelKey: 'Sidebar.IamsAgentRuns', href: '/audit/iams/agent-runs', icon: (props: any) => <FaRobot {...props} /> },
      { page: 'iams-copilot', labelKey: 'Sidebar.IamsCopilot', href: '/audit/iams/copilot-connector', icon: (props: any) => <FaRobot {...props} /> },
      { page: 'iams-trust-center', labelKey: 'Sidebar.IamsTrustCenter', href: '/audit/iams/trust-center', icon: (props: any) => <FaShieldAlt {...props} /> },
      { page: 'iams-admin', labelKey: 'Sidebar.IamsAdmin', href: '/audit/iams/admin', icon: (props: any) => <LuSettings {...props} /> },
      { page: 'iams-workflows', labelKey: 'Sidebar.IamsWorkflows', href: '/audit/iams/admin/workflows', icon: (props: any) => <FaProjectDiagram {...props} /> },
      { page: 'iams-bulk-import', labelKey: 'Sidebar.IamsBulkImport', href: '/audit/iams/admin/import', icon: (props: any) => <FaFileAlt {...props} /> },
    ],
  },
  */


  /* Sup-Compliance menu — hidden from sidebar; re-enable when needed
  {
    page: 'sup-compliance',
    labelKey: 'Sidebar.SupCompliance',
    href: '#',
    icon: (props: any) => <LuShieldCheck {...props} />,
    children: [
      { page: 'sup-dashboard', labelKey: 'Sidebar.Dashboard', href: '/compliances/dashboard', icon: (props: any) => <LuLayoutDashboard {...props} /> },
      { page: 'sup-reports', labelKey: 'Sidebar.Reports', href: '/reportSup', icon: (props: any) => <LuFileBarChart {...props} /> },
      // { page: 'sup-standards', labelKey: 'Sidebar.Reference', href: '/StandardsSupCompliance', icon: (props: any) => <LuGrid {...props} /> },
      { page: 'sup-tickets', labelKey: 'Sidebar.Tickets', href: '/ticketsSupCompliance', icon: (props: any) => <LuTicket {...props} /> },
      { page: 'sup-assessment', labelKey: 'Sidebar.Assessment', href: '/assessmentSup', icon: (props: any) => <FaChartBar {...props} /> },
      { page: 'sup-certifications', labelKey: 'Sidebar.Certifications', href: '/certificationsSup', icon: (props: any) => <PiCertificate {...props} /> },
      { page: 'sup-announcements', labelKey: 'Sidebar.Announcements', href: '/announcementSup', icon: (props: any) => <FaBullhorn {...props} /> },
      { page: 'sup-institutions', labelKey: 'Sidebar.SupComplianceInstitutions', href: '/institutionSup', icon: (props: any) => <LuBuilding2 {...props} /> },
    ],
  },
  */
  // {
  //   page: 'organizations',
  //   labelKey: 'Sidebar.Organizations',
  //   href: '#',
  //   icon: (props: any) => <LuBuilding2 {...props} />,
  //   children: [
      // {
      //   page: 'organizations-home',
      //   labelKey: 'Sidebar.OrganizationsHome',
      //   href: '/tenants/tree',
      //   icon: (props: any) => <FaSitemap {...props} />,
      // },
      // {
      //   page: 'tenant-compliance-standards',
      //   labelKey: 'Sidebar.OrganizationsStandards',
      //   href: '/tenants/standards',
      //   icon: (props: any) => <LuShieldCheck {...props} />,
      // },
      // {
      //   page: 'organizations-tickets',
      //   labelKey: 'Sidebar.OrganizationsTickets',
      //   href: '/tenants/tickets',
      //   icon: (props: any) => <LuTicket {...props} />,
      // },
      // {
      //   page: 'organizations-federations',
      //   labelKey: 'Sidebar.OrganizationsFederations',
      //   href: '/federations',
      //   icon: (props: any) => <LuNetwork {...props} />,
      // },
  //     {
  //       page: 'organizations-settings',
  //       labelKey: 'Sidebar.OrganizationsSettings',
  //       href: '/tenants/settings',
  //       icon: (props: any) => <LuSettings {...props} />,
  //     },
  //   ],
  // },
  /* Admin Partners directory removed — pages were demo-only; rebuild against
     real /api/admin/partners/* endpoints when they exist. */
  /* User Guide — hidden from sidebar; re-enable when needed
  {
    page: 'guide',
    labelKey: 'Sidebar.UserGuide',
    href: '/guide',
    icon: (props: any) => <FaBook {...props} />
  },
  */
  // Pianat Admin (platform_operator console) lives in the standalone
  // pianat_admin_frontend app — intentionally absent from the customer sidebar.
  // Branch (Phase 5) — sub-tenant operations: inter-branch collaboration +
  // the daily Branch Ops agent. Only for tenants WITH a parent.
  // Order requested: Workflow → SLA → Configuration.
  { page: 'workflow-admin', labelKey: 'Sidebar.Workflow', href: '/workflows', icon: (props: any) => <FaClipboardCheck {...props} />, permissionPage: 'Workflow' },
  {
    page: 'sla',
    labelKey: 'Sidebar.SLA',
    href: '#',
    icon: (props: any) => <LuActivity {...props} />,
    children: [
      { page: 'sla-tasks', labelKey: 'Sidebar.SlaMonitor', href: '/sla-tasks', icon: (props: any) => <FaTasks {...props} />, permissionPage: 'SLA' },
      { page: 'sla-workflows', labelKey: 'Sidebar.SlaWorkflows', href: '/sla-workflows', icon: (props: any) => <FaProjectDiagram {...props} />, permissionPage: 'SLA' },
    ],
  },
  // Removed from the menu: the Tenable Vulnerabilities browser. Nobody read tens of
  // thousands of findings a screenful at a time - they exported them - so the export is
  // now a button on Configuration -> Integration Settings, beside the connection it
  // reads through, and the browser is gone.
  {
    page: 'configuration',
    labelKey: 'Sidebar.Configuration',
    href: '#',
    icon: (props: any) => <LuSettings {...props} />,
    children: configurationSidebarChildren,
    permissionPage: 'Settings',
  },
  { page: 'organization-chart', labelKey: 'Sidebar.OrganizationChart', href: '/organization-chart', icon: (props: any) => <FaSitemap {...props} />, permissionPage: 'Users' },
  { page: 'users', labelKey: 'Sidebar.UserManagement', href: '/user', icon: (props: any) => <LuUserCog {...props} />, permissionPage: 'Users' },
  { page: 'user-permissions', labelKey: 'Sidebar.UserPermissions', href: '/user/permissions', icon: (props: any) => <LuSettings {...props} />, permissionPage: 'Users' },
  { page: 'permission-groups', labelKey: 'Sidebar.Groups', href: '/permission/groups', icon: (props: any) => <FaUsers {...props} />, permissionPage: 'Users' },
  { page: 'siem-log', labelKey: 'Sidebar.SiemLog', href: '/audit/siem-log', icon: (props: any) => <FaHistory {...props} />, permissionPage: 'SIEM Log' },
  // Functions moved to the Configuration submenu (CONFIGURATION_SECTION_NAV).
  // Workflow moved up — it now leads the Workflow → SLA → Configuration block.
  /* Branch menu — hidden from sidebar; routes disabled in main.tsx. Re-enable both together.
  {
    page: 'sub-tenant-section',
    labelKey: 'Sidebar.Branch',
    href: '#',
    icon: (props: any) => <FaBuilding {...props} />,
    requiresSubTenantDepth: true,
    children: [
      {
        page: 'st-inter-branch',
        labelKey: 'Sidebar.BranchInterBranch',
        href: '/sub-tenant/inter-branch',
        icon: (props: any) => <LuTicket {...props} />,
      },
      {
        page: 'st-branch-ops',
        labelKey: 'Sidebar.BranchOpsAgent',
        href: '/sub-tenant/branch-ops-agent',
        icon: (props: any) => <FaRobot {...props} />,
      },
    ],
  },
  */
  // Tenant Admin (Phase 4) — parent tenants managing their sub-tenant tree.
  // Double-gated: canManageMemberships capability + subtree scope; backend
  // enforces the same via @TenantOwnerOnly so hiding here is UX, not security.
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, language, mobileOpen = false, onMobileClose }) => {
  const location = useLocation()
  const { t } = useTranslation()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const isRTL = language === 'ar'
  const closeMobile = () => onMobileClose?.()

  // Signed-in user shown in the bottom sidebar card. Sourced from /auth/me
  // (the same server-verified session ProtectedRoute checks) rather than
  // decoding the local JWT mirror — that mirror is cleared independently of
  // the real session, which used to leave this card stuck on a generic
  // "User / Member" placeholder even while a real session was active.
  const [userInfo, setUserInfo] = useState<{ name: string; role: string; initials: string }>({ name: 'User', role: 'Member', initials: 'U' })

  useEffect(() => {
    let cancelled = false
    const loadUserInfo = () => {
      getUserData().then((user) => {
        if (cancelled) return
        if (!user) {
          setUserInfo({ name: 'User', role: 'Member', initials: 'U' })
          return
        }
        const name = user.name || user.username || user.email || 'User'
        const role = user.title || prettifyGroup(user.groupName) || 'Member'
        const initials = String(name).trim().split(/\s+/).map((p: string) => p[0]).slice(0, 2).join('').toUpperCase() || 'U'
        setUserInfo({ name: String(name), role: String(role), initials })
      })
    }
    loadUserInfo()
    window.addEventListener('comply:auth-changed', loadUserInfo)
    return () => {
      cancelled = true
      window.removeEventListener('comply:auth-changed', loadUserInfo)
    }
  }, [])
  const [demoAppUrl, setDemoAppUrl] = useState<string>('http://localhost:3001/dcc')
  const { currentTenant } = useTenant()
  const archetype = currentTenant?.archetype ?? currentTenant?.rootEntityType ?? null
  // Partner Portal moved to the standalone partner_portal_frontend app, so this
  // customer app always renders the full client menu.
  const baseMenuLinks = links

  // Filter out modules disabled by the parent tenant. The /tenant/me endpoint
  // returns `disabledModules: string[]` — any top-level link whose `page` key
  // is in that list is hidden. Children are filtered recursively.
  const disabledModules = useRef<Set<string>>(new Set())
  disabledModules.current = new Set(currentTenant?.disabledModules ?? [])

  // TrustLens-only default removed — all modules visible for UB deployment.

  // Filter out links whose archetypeGate doesn't match the caller's archetype.
  // Per the Trust Network design, Evidence Vault + Trust Center are only for
  // `client` archetypes; Practitioners + Playbooks are only for consulting/audit.
  const caps = capabilitiesFor(archetype)

  // Phase 4: subtree-scope gate for the Tenant Admin section. /tenant/me
  // returns allowedScopes; leaves are own-only and have nothing to administer.
  const hasSubtreeScope = Boolean(
    (currentTenant as any)?.allowedScopes?.includes?.('subtree'),
  )
  // Phase 5: branch gate — the caller's tenant has a parent. /tenant/me
  // returns tenantDepth (root = 0) and parentTenantId.
  const isSubTenant = Boolean(
    ((currentTenant as any)?.tenantDepth ?? 0) > 0 ||
      (currentTenant as any)?.parentTenantId,
  )

  // ── Permission gate ───────────────────────────────────────────────────────
  // A menu that offers what the user cannot open is not a permission-based UI:
  // the link stays, they click it, and the page refuses. Links whose
  // `permissionPage` the caller lacks `show` on are hidden here instead.
  //
  // FAIL CLOSED. `null` means "no permission answer yet" and hides every gated
  // link — only Home and ungated links show. This used to be fail-open (null
  // showed the full menu), so after a refresh, while the request was in flight
  // or whenever it failed (CSRF/token not ready yet, network blip), a group saw
  // the pages its permissions had closed. An empty instant is acceptable; a
  // leaked link is not.
  //
  // Permissions are re-fetched on every route change while the menu keeps the
  // current answer on screen (Main keeps this component mounted across
  // navigation once the session is verified), so a permission an admin revokes
  // disappears on the next navigation without the menu collapsing. A failed
  // re-fetch keeps the current answer — it never adds links. No module-level
  // cache: this component unmounts on sign-out and logout is a full reload, so
  // one user's menu can never carry over to the next.
  const [permittedPages, setPermittedPages] = useState<Set<string> | null>(null)
  // Still used below for the adminOnly filter (screens like Integration
  // Settings whose backend endpoints reject non-admins outright, whatever a
  // group's page permissions say) — see hasFullAccess() in the backend's
  // auth/admin-groups.ts. It no longer bypasses the `show`-based menu filter
  // itself: an admin_/super_admin_ group's permission rows are real, curated
  // entries here (an admin can and does have `show: false` on specific
  // pages — SIEM Log, Data Sheets, etc. — and expects the sidebar to honor
  // that), not the all-false placeholder rows the old bypass assumed.
  //
  // Which groups those are is NOT decided here: this file carried its own copy of the
  // list and its own normalisation until 2026-09-22, which is one more place to miss when
  // the admin groups change — and they did change that day, swapping roles between the
  // two names. utils/adminGroup.ts is the single copy.
  const isFullAccess = isAdminGroup(getUserRole())

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const retry = (attempt: number) => {
      if (!cancelled && attempt < 5) retryTimer = setTimeout(() => load(attempt + 1), 1500 * (attempt + 1))
    }
    const load = (attempt: number) => {
      getUserRolePermissions()
        .then((res: any) => {
          if (cancelled) return
          // A dead session or a network blip is not a permission answer — keep
          // what we already have (the last answer, or nothing) and try again.
          // Never widen the menu on a failure.
          if (!res?.success) return retry(attempt)
          const rows: any[] = res?.data?.permissions ?? []
          const granted = new Set<string>()
          for (const row of rows) {
            if (row?.show === true && typeof row?.page === 'string') {
              granted.add(row.page.trim().toLowerCase())
            }
          }
          setPermittedPages(granted)
        })
        .catch(() => retry(attempt))
    }
    load(0)
    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [location.pathname])

  /** True when the caller may see this link. */
  const isPermitted = (l: SidebarLink): boolean => {
    // Home is the one guaranteed escape route — never hide it (same principle
    // as ALWAYS_ALLOW in ModuleGate and LANDING_PAGE in the backend catalog).
    if (l.page === 'dashboard-home') return true
    // Rendered disabled for everyone, so there is nothing to grant.
    if (l.unavailable) return true
    if (!l.permissionPage) return true
    // No permission answer yet (or the request failed) → hide every gated link.
    if (permittedPages === null) return false
    const names = Array.isArray(l.permissionPage) ? l.permissionPage : [l.permissionPage]
    // An array means the link fronts several screens (the ORM app): any one of
    // them granting `show` is enough to earn the link.
    return names.some((n) => permittedPages.has(String(n).trim().toLowerCase()))
  }

  const filterLinks = (items: SidebarLink[]): SidebarLink[] =>
    items
      .filter((l) => !disabledModules.current.has(l.page))
      .filter((l) => !l.archetypeGate || caps[l.archetypeGate])
      .filter((l) => !l.requiresSubtree || hasSubtreeScope)
      .filter((l) => !l.requiresSubTenantDepth || isSubTenant)
      // Admin-only links stay hidden for everyone else, whatever their page permissions say.
      .filter((l) => !l.adminOnly || isFullAccess)
      .filter(isPermitted)
      .map((l) => (l.children ? { ...l, children: filterLinks(l.children) } : l))
      // A pure group (href '#', no page of its own) left with zero visible
      // children is a dead expandable arrow — drop it too.
      .filter((l) => !(l.href === '#' && l.children && l.children.length === 0))

  const menuLinks = filterLinks(baseMenuLinks)

  useEffect(() => {
    fetch('/config.json')
      .then((res) => res.ok ? res.json() : {})
      .then((config: { DEMO_APP_URL?: string }) => {
        if (config.DEMO_APP_URL) setDemoAppUrl(config.DEMO_APP_URL.replace(/\/$/, ''))
      })
      .catch(() => {})
  }, [])

  // Which parent group is expanded (e.g. 'catalogs')
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // Which sub-group (menu inside menu) is expanded (e.g. 'user-settings-permission', 'policy-module')
  const [openSubGroup, setOpenSubGroup] = useState<string | null>(null)

  // Keep groups open when on a child route
  useEffect(() => {
    const path = location.pathname
    if (path.startsWith('/user') || path.startsWith('/team-onboarding') || path.startsWith('/permission')) {
      setOpenGroup('user-settings')
      if (path.startsWith('/user/permissions') || path.startsWith('/permission')) {
        setOpenSubGroup('user-settings-permission')
      } else {
        setOpenSubGroup(null)
      }
    } else if (
      path === '/compliances/dashboard' ||
      path.startsWith('/reporting') ||
      path.startsWith('/standards') ||
      path.startsWith('/ticket') ||
      (path.startsWith('/assessment') && !path.startsWith('/assessment-survey')) ||
      path.startsWith('/certifications') ||
      path.startsWith('/announcements') ||
      path.startsWith('/organization-chart')
    ) {
      setOpenGroup('sup-compliance')
      setOpenSubGroup(null)
    } else if (path.startsWith('/function') || path.startsWith('/QuestionControlTree') || path.startsWith('/document-categories') || path.startsWith('/document-approval') || path.startsWith('/configuration/')) {
      setOpenGroup('configuration')
      setOpenSubGroup(null)
    } else if (path.startsWith('/workflows')) {
      // Workflow is its own top-level link (not a Configuration child) — close
      // whatever group was previously open instead of leaving it expanded.
      setOpenGroup(null)
      setOpenSubGroup(null)
    } else if (path.startsWith('/data-inventory') || path.startsWith('/catalogs/') || path === '/catalogs' || path.startsWith('/controls') || path.startsWith('/catalogs/resources') || path.startsWith('/assets') || path === '/risks') {
      setOpenGroup('catalogs')
      setOpenSubGroup(null)
    } else if (path.startsWith('/policy/categories') || path.startsWith('/policy/settings')) {
      setOpenGroup('configuration')
      setOpenSubGroup(null)
    } else if (path.startsWith('/standards') || path.startsWith('/objective') || path.startsWith('/compliances') || path.startsWith('/announcements') || path.startsWith('/ticket') || path.startsWith('/impactarea') || path.startsWith('/viewsurvey') || path.startsWith('/createsurvey') || path.startsWith('/updatesurvey') || path.startsWith('/surveys') || path.startsWith('/create-survey') || path.startsWith('/bankQuestions') || path.startsWith('/exportsurvey') || path.startsWith('/assessmentsurvey') || path.startsWith('/assessment-survey') || path.startsWith('/export-survey') || path.startsWith('/createQuestion') || path.startsWith('/updateQuestion') || path.startsWith('/QuestionControlTree') || path.startsWith('/policy') || path.startsWith('/remediation')) {
      setOpenGroup('compliance')
      if (path.startsWith('/policy') && !path.startsWith('/policy/audit')) {
        setOpenSubGroup('policy-module')
      } else if (
        path.startsWith('/surveys') ||
        path.startsWith('/create-survey') ||
        path.startsWith('/bankQuestions') ||
        path.startsWith('/viewsurvey') ||
        path.startsWith('/createsurvey') ||
        path.startsWith('/updatesurvey') ||
        path.startsWith('/exportsurvey') ||
        path.startsWith('/assessmentsurvey') ||
        path.startsWith('/assessment-survey') ||
        path.startsWith('/export-survey') ||
        path.startsWith('/createQuestion') ||
        path.startsWith('/updateQuestion') ||
        path.startsWith('/QuestionControlTree')
      ) {
        setOpenSubGroup('survey-module')
      } else {
        setOpenSubGroup(null)
      }
    } else if (path.startsWith('/sla-tasks') || path.startsWith('/sla-workflows')) {
      setOpenGroup('sla')
      setOpenSubGroup(null)
    }
  }, [location.pathname])



 
   

  useEffect(() => {
    if (isOpen && sidebarRef.current) {
      const activeItem = sidebarRef.current.querySelector('[data-active="true"]')
      activeItem?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [location.pathname, isOpen])

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`d-none d-md-flex flex-column h-100 position-relative ${
        isOpen ? 'sidebar-expanded' : 'sidebar-collapsed'
      } ${isRTL ? 'rtl' : 'ltr'}`}
     
    >
      <div className="sidebar-logo-wrap">
        <Link to="/" className="sidebar-brand">
          {isOpen ? (
            <span className="sidebar-brand-stack">
              <img
                src={process.env.PUBLIC_URL + '/assets/images/2nd Logo Light.svg'}
                alt="logo"
                className="logocomply"
              />
              <span className="sidebar-tenant-logo-card">
                <img
                  src={process.env.PUBLIC_URL + '/assets/images/khalda-logo.png'}
                  alt="Khalda Petroleum Company"
                  className="sidebar-tenant-logo"
                />
              </span>
            </span>
          ) : (
            <img
              src={process.env.PUBLIC_URL + '/assets/images/Shield Icon green.svg'}
              alt="logo"
              className="logoCollapsed"
            />
          )}
        </Link>
      </div>

      <div ref={sidebarRef} className="overflow-auto">
        {menuLinks.map((link) => {
          const active = location.pathname === link.href
          const hasChildren = link.children && link.children.length > 0
          const isGroupOpen = hasChildren && openGroup === link.page
          // Section group labels (GOVERNANCE / CATALOGS / …) removed from the sidebar.
          const sectionEl = null

          // Parent with children (e.g. Data Inventory)
          if (hasChildren && link.children) {
            const handleParentClick = () => {
              if (!isOpen) {
                onToggle()
                setOpenGroup(link.page)
              } else {
                setOpenGroup((prev) => (prev === link.page ? null : link.page))
              }
            }
            return (
              <div key={link.page}>
                {sectionEl}
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-group ${isGroupOpen ? 'active' : ''} ${!isOpen ? 'justify-content-center' : ''}`}
                  onClick={handleParentClick}
                  title={!isOpen ? String(t(link.labelKey)) : ''}
                  style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer', flexDirection: 'row', direction: 'inherit', textAlign: isRTL ? 'right' : 'left' }}
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                  })}
                  {isOpen && (
                    <>
                      <span className="text-truncate flex-grow-1">{t(link.labelKey)}</span>
                      {isGroupOpen ? (
                        <FaChevronDown className="sidebar-group-chevron" aria-hidden />
                      ) : (
                        <FaChevronRight className="sidebar-group-chevron" aria-hidden />
                      )}
                    </>
                  )}
                </button>
                {isOpen && isGroupOpen && (
                  <div className="sidebar-children">
                    {link.children.map((child) => {
                      const childActive =
                        location.pathname === child.href ||
                        (child.href !== "/" && location.pathname.startsWith(child.href))
                      const hasGrandchildren = child.children && child.children.length > 0
                      if (hasGrandchildren && child.children) {
                        const isSubOpen = openSubGroup === child.page
                        const handleSubClick = () => {
                          setOpenSubGroup((prev) => (prev === child.page ? null : child.page))
                        }
                        return (
                          <div key={child.page}>
                            <button
                              type="button"
                              className={`sidebar-link sidebar-link-child sidebar-link-subgroup ${isSubOpen ? 'active' : ''}`}
                              onClick={handleSubClick}
                              title={String(t(child.labelKey))}
                              style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer', flexDirection: 'row', direction: 'inherit', textAlign: isRTL ? 'right' : 'left' }}
                            >
                              {React.createElement(child.icon, { className: 'sidebar-icon mx-2', style: { fontSize: '0.78rem' } })}
                              <span className="text-truncate flex-grow-1">{t(child.labelKey)}</span>
                              {isSubOpen ? (
                                <FaChevronDown className="sidebar-group-chevron" aria-hidden style={{ fontSize: '0.62rem' }} />
                              ) : (
                                <FaChevronRight className="sidebar-group-chevron" aria-hidden style={{ fontSize: '0.62rem' }} />
                              )}
                            </button>
                            {isSubOpen && (
                              <div className="sidebar-children sidebar-children-nested">
                                {child.children.map((grandchild) => {
                                  const grandActive = location.pathname === grandchild.href || (grandchild.href !== '/' && location.pathname.startsWith(grandchild.href))
                                  return (
                                    <Link
                                      key={grandchild.href}
                                      to={grandchild.href}
                                      className={`sidebar-link sidebar-link-child ${grandActive ? 'active' : ''}`}
                                      data-active={grandActive}
                                      title={String(t(grandchild.labelKey))}
                                      style={{ flexDirection: 'row', direction: 'inherit' }}
                                    >
                                      {React.createElement(grandchild.icon, { className: 'sidebar-icon mx-2', style: { fontSize: '0.72rem' } })}
                                      <span className="text-truncate">{t(grandchild.labelKey)}</span>
                                    </Link>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      }
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`sidebar-link sidebar-link-child${child.descriptionKey ? ' sidebar-link-child--stacked' : ''} ${childActive ? 'active' : ''}`}
                          data-active={childActive}
                          title={
                            child.descriptionKey
                              ? `${String(t(child.labelKey))} — ${String(t(child.descriptionKey))}`
                              : String(t(child.labelKey))
                          }
                          style={{ flexDirection: 'row', direction: 'inherit' }}
                        >
                          {React.createElement(child.icon, {
                            className: 'sidebar-icon mx-2 sidebar-link-child__icon',
                            style: { fontSize: '0.78rem' },
                          })}
                          {child.descriptionKey ? (
                            <span className="sidebar-link-label-stack flex-grow-1">
                              <span className="text-truncate d-block">{t(child.labelKey)}</span>
                              <span className="sidebar-link-desc">{t(child.descriptionKey)}</span>
                            </span>
                          ) : (
                            <span className="text-truncate">{t(child.labelKey)}</span>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }

          // A module this deployment does not include: greyed out, not a link.
          if (link.unavailable) {
            const reason = String(t('Sidebar.moduleUnavailable', { defaultValue: "You don't have access to this module" }))
            return (
              <div key={link.href || link.page}>
                {sectionEl}
                <span
                  className={`sidebar-link sidebar-link--disabled ${!isOpen ? 'justify-content-center' : ''}`}
                  role="link"
                  aria-disabled="true"
                  title={!isOpen ? `${t(link.labelKey)} — ${reason}` : reason}
                  style={{ flexDirection: 'row', direction: 'inherit', opacity: 0.45, cursor: 'not-allowed' }}
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                  })}
                  {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
                </span>
              </div>
            )
          }

          // Handle external links
          if (link.external && (link.externalUrl != null || link.page === 'enterprise-risk-management')) {
            const baseUrl = link.page === 'enterprise-risk-management' ? demoAppUrl : (link.externalUrl || link.href)
            const token = getV2Token()
            const externalUrl = link.page === 'enterprise-risk-management' && token
              ? `${baseUrl}#auth=${encodeURIComponent(token)}`
              : baseUrl
            const sameTab = link.openInSameTab === true || link.page === 'enterprise-risk-management'
            return (
              <div key={link.href || link.page}>
                {sectionEl}
                <a
                  href={externalUrl}
                  target={sameTab ? '_self' : '_blank'}
                  rel={sameTab ? undefined : 'noopener noreferrer'}
                  className={`sidebar-link ${!isOpen ? 'justify-content-center' : ''}`}
                  title={!isOpen ? String(t(link.labelKey)) : ''}
                  style={{ flexDirection: 'row', direction: 'inherit' }}
                >
                  {React.createElement(link.icon, {
                    className: "sidebar-icon mx-2",
                    style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                  })}
                  {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
                </a>
              </div>
            )
          }

          return (
            <div key={link.href}>
              {sectionEl}
              <Link
                to={link.href}
                className={`sidebar-link ${active ? 'active' : ''} ${!isOpen ? 'justify-content-center' : ''}`}
                data-active={active}
                title={!isOpen ? String(t(link.labelKey)) : ''}
                style={{ flexDirection: 'row', direction: 'inherit' }}
              >
                {React.createElement(link.icon, {
                  className: "sidebar-icon mx-2",
                  style: { fontSize: !isOpen ? '1.65rem' : '1.2rem' }
                })}
                {isOpen && <span className="text-truncate">{t(link.labelKey)}</span>}
              </Link>
            </div>
          )
        })}
      </div>

      <div className="sidebar-user" title={`${userInfo.name} — ${userInfo.role}`}>
        <span className="sidebar-user-avatar">{userInfo.initials}</span>
        {isOpen && (
          <span className="sidebar-user-meta">
            <span className="sidebar-user-name">{userInfo.name}</span>
            <span className="sidebar-user-role">{userInfo.role}</span>
          </span>
        )}
      </div>

      {mobileOpen && (
        <>
        <div className="sidebar-backdrop d-md-none" onClick={closeMobile} />
        <div
          className="position-fixed top-0 start-0 h-100 sidebar-mobile d-md-none"
          style={{ zIndex: 1100 }}
        >
          <div className="d-flex align-items-center justify-content-between sidebar-mobile-head">
            <span className="sidebar-brand-stack">
              <img src={process.env.PUBLIC_URL + '/assets/images/2nd Logo Light.svg'} alt="logo" className="logocomply" />
              <span className="sidebar-tenant-logo-card">
                <img src={process.env.PUBLIC_URL + '/assets/images/khalda-logo.png'} alt="Khalda Petroleum Company" className="sidebar-tenant-logo" />
              </span>
            </span>
            <button className="sidebar-mobile-close" onClick={closeMobile} aria-label="Close menu">✕</button>
          </div>
          <div className="sidebar-mobile-body">
            {menuLinks.map((link) => {
              const active = location.pathname === link.href
              const hasChildren = link.children && link.children.length > 0

              // Parent with children in mobile menu
              if (hasChildren && link.children) {
                return (
                  <div key={link.page}>
                    <div className="py-2 fw-semibold text-secondary">
                      {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                    </div>
                    {link.children.map((child) => {
                      const childActive =
                        location.pathname === child.href ||
                        (child.href !== "/" && location.pathname.startsWith(child.href))
                      const hasGrandchildren = child.children && child.children.length > 0
                      if (hasGrandchildren && child.children) {
                        return (
                          <div key={child.page}>
                            <div className="py-1 ps-4 small fw-semibold text-muted">{t(child.labelKey)}</div>
                            {child.children.map((grandchild) => {
                              const grandActive = location.pathname === grandchild.href || (grandchild.href !== '/' && location.pathname.startsWith(grandchild.href))
                              return (
                                <Link
                                  key={grandchild.href}
                                  to={grandchild.href}
                                  className={`d-block py-2 ps-5 text-decoration-none ${grandActive ? 'fw-bold text-primary' : ''}`}
                                  onClick={closeMobile}
                                >
                                  {React.createElement(grandchild.icon, { className: "me-2" })} {t(grandchild.labelKey)}
                                </Link>
                              )
                            })}
                          </div>
                        )
                      }
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`d-block py-2 ps-4 text-decoration-none ${childActive ? 'fw-bold text-primary' : ''}`}
                          onClick={closeMobile}
                        >
                          <span className="d-flex align-items-start gap-2">
                            {React.createElement(child.icon, { className: 'me-2 flex-shrink-0 mt-1' })}
                            <span>
                              <span className="d-block">{t(child.labelKey)}</span>
                              {child.descriptionKey && (
                                <span className="d-block small text-muted fw-normal">{t(child.descriptionKey)}</span>
                              )}
                            </span>
                          </span>
                        </Link>
                      )
                    })}
                  </div>
                )
              }

              // Unavailable module in the mobile menu: greyed out, with the reason under it
              // (touch screens have no hover tooltip).
              if (link.unavailable) {
                return (
                  <span
                    key={link.href || link.page}
                    className="d-block py-2"
                    role="link"
                    aria-disabled="true"
                    style={{ opacity: 0.45, cursor: 'not-allowed' }}
                  >
                    {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                    <span className="d-block small text-muted">
                      {t('Sidebar.moduleUnavailable', { defaultValue: "You don't have access to this module" })}
                    </span>
                  </span>
                )
              }

              // Handle external links in mobile menu
              if (link.external && (link.externalUrl != null || link.page === 'enterprise-risk-management')) {
                const baseUrl = link.page === 'enterprise-risk-management' ? demoAppUrl : (link.externalUrl || link.href)
                const token = getV2Token()
                const externalUrl = link.page === 'enterprise-risk-management' && token
                  ? `${baseUrl}#auth=${encodeURIComponent(token)}`
                  : baseUrl
                const sameTab = link.openInSameTab === true || link.page === 'enterprise-risk-management'
                return (
                  <a
                    key={link.href || link.page}
                    href={externalUrl}
                    target={sameTab ? '_self' : '_blank'}
                    rel={sameTab ? undefined : 'noopener noreferrer'}
                    className="d-block py-2 text-decoration-none"
                    onClick={closeMobile}
                  >
                    {React.createElement(link.icon, { className: "me-2" })} {t(link.labelKey)}
                  </a>
                )
              }

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`d-block py-2 text-decoration-none ${active ? 'fw-bold text-primary' : ''}`}
                  onClick={closeMobile}
                >
                  <span className="d-inline-flex align-items-center w-100">
                    {React.createElement(link.icon, { className: "me-2" })}
                    <span className="flex-grow-1">{t(link.labelKey)}</span>
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

export default Sidebar